import * as Phaser from "phaser"
import { InputManager } from "../core/input-mapper"
import { AUDIO, SCENE } from "../core/keys"
import { THEME } from "../core/theme"
import { GAME_HEIGHT, GAME_WIDTH } from "../data/constants"
import { playSfx } from "../systems/sfx-generator"
import { NEON, drawScanlines } from "../ui/neon-fx"

const FONT = THEME.fonts
const COLOR = THEME.colors

interface SectorClearData {
  waveNumber: number
  sectorName: string
  enemiesKilled: number
  scoreEarned: number
  coinsCollected: number
}

export class SectorClearScene extends Phaser.Scene {
  private clearData!: SectorClearData
  private dismissed = false
  private inputManager!: InputManager
  private continueText!: Phaser.GameObjects.Text
  private autoDismissTimer!: Phaser.Time.TimerEvent

  constructor() {
    super({ key: SCENE.SECTOR_CLEAR })
  }

  init(data: SectorClearData): void {
    this.clearData = data
    this.dismissed = false
  }

  create(): void {
    const cx = GAME_WIDTH / 2
    const cy = GAME_HEIGHT / 2

    this.cameras.main.setBackgroundColor(0x000000)

    this.inputManager = new InputManager(this, {
      onDown: (action) => {
        if (
          action === "CONFIRM" ||
          action === "FLAP" ||
          action === "ADVANCE" ||
          action === "BACK"
        ) {
          this.dismiss()
        }
      },
    })
    this.inputManager.bindScenePointer(
      "pointerdown",
      (p: Phaser.Input.Pointer) => {
        p.event.stopPropagation()
      },
    )
    this.events.once("shutdown", () => {
      this.inputManager.destroy()
    })

    drawScanlines(this, 0, 0, GAME_WIDTH, GAME_HEIGHT, 0.04, 3).setDepth(5)

    const bg = this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0)
    this.tweens.add({ targets: bg, alpha: 0.88, duration: 300 })

    this.createTopBanner(cx)
    this.createStatsPanel(cx, cy)
    this.createBottomSection(cx)

    this.playClearJingle()

    this.autoDismissTimer = this.time.delayedCall(6000, () => {
      if (!this.dismissed) {
        this.dismiss()
      }
    })
  }

  private playClearJingle(): void {
    playSfx(AUDIO.SFX_VICTORY, 1.0)
  }

  private createTopBanner(cx: number): void {
    const topRule = this.add.rectangle(cx, 96, 420, 2, NEON.green, 0.95)
    const bottomRule = this.add.rectangle(cx, 166, 520, 1, NEON.green, 0.45)
    const title = this.add
      .text(cx, 126, "SECTOR CLEAR", {
        fontFamily: FONT.pixel,
        fontSize: "26px",
        color: COLOR.successText,
      })
      .setOrigin(0.5)
      .setAlpha(0)
    const subtitle = this.add
      .text(cx, 158, this.clearData.sectorName.toUpperCase(), {
        fontFamily: FONT.mono,
        fontSize: "13px",
        color: COLOR.warningText,
        letterSpacing: 4,
      })
      .setOrigin(0.5)
      .setAlpha(0)

    this.tweens.add({
      targets: [title, subtitle],
      alpha: 1,
      y: "-=6",
      duration: 340,
      delay: 180,
      ease: "Cubic.easeOut",
    })

    this.tweens.add({
      targets: [topRule, bottomRule],
      scaleX: { from: 0.2, to: 1 },
      duration: 280,
      ease: "Cubic.easeOut",
    })
  }

  private createStatsPanel(cx: number, cy: number): void {
    const panelY = cy + 12
    const waveLabel = this.add
      .text(cx, panelY - 82, `WAVE ${this.clearData.waveNumber} COMPLETE`, {
        fontFamily: FONT.mono,
        fontSize: "12px",
        color: COLOR.mutedTextLighter,
        letterSpacing: 3,
      })
      .setOrigin(0.5)
      .setAlpha(0)

    const stats = [
      {
        label: "ENEMIES",
        value: this.clearData.enemiesKilled.toLocaleString(),
      },
      {
        label: "SCORE",
        value: this.clearData.scoreEarned.toLocaleString(),
      },
      {
        label: "COINS",
        value: `+${this.clearData.coinsCollected}`,
      },
    ]

    this.tweens.add({
      targets: waveLabel,
      alpha: 1,
      duration: 240,
      delay: 360,
    })

    for (let i = 0; i < stats.length; i++) {
      const stat = stats[i]
      const cellX = cx - 180 + i * 180
      const cellY = panelY
      const bg = this.add.graphics()
      bg.fillStyle(COLOR.black, 0.58)
      bg.fillRoundedRect(cellX - 74, cellY - 42, 148, 84, 6)
      bg.lineStyle(1, NEON.green, 0.4)
      bg.strokeRoundedRect(cellX - 74, cellY - 42, 148, 84, 6)
      bg.setAlpha(0)

      const label = this.add
        .text(cellX, cellY - 20, stat.label, {
          fontFamily: FONT.sans,
          fontSize: "10px",
          color: COLOR.mutedTextSoft,
          letterSpacing: 2,
        })
        .setOrigin(0.5)
        .setAlpha(0)

      const value = this.add
        .text(cellX, cellY + 10, stat.value, {
          fontFamily: FONT.mono,
          fontSize: "24px",
          color: COLOR.successText,
          fontStyle: "900",
        })
        .setOrigin(0.5)
        .setAlpha(0)

      this.tweens.add({
        targets: [bg, label, value],
        alpha: 1,
        y: "-=8",
        duration: 260,
        delay: 460 + i * 90,
        ease: "Cubic.easeOut",
      })
    }
  }

  private createBottomSection(cx: number): void {
    this.continueText = this.add
      .text(cx, GAME_HEIGHT - 92, "PRESS ANY ACTION", {
        fontFamily: FONT.mono,
        fontSize: "11px",
        color: COLOR.mutedTextLighter,
        letterSpacing: 4,
      })
      .setOrigin(0.5)
      .setAlpha(0)

    this.tweens.add({
      targets: this.continueText,
      alpha: 1,
      duration: 300,
      delay: 1000,
    })

    this.tweens.add({
      targets: this.continueText,
      alpha: 0.3,
      duration: 400,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    })
  }

  private dismiss(): void {
    if (this.dismissed) return
    this.dismissed = true

    if (this.autoDismissTimer) {
      this.autoDismissTimer.remove()
    }

    this.tweens.add({
      targets: this.cameras.main,
      alpha: 0,
      duration: 200,
      onComplete: () => {
        this.scene.stop()
      },
    })
  }
}
